import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import apiClient from '../utils/apiInterceptor';
import { getConnectionStatus, subscribeConnection, watchConnection } from '../utils/connectionStatus';
import { installOffline, getOfflineState, subscribeOffline, dismissFailures, clearSavedCopyNotice } from '../utils/offline';
import { useAuth } from '../contexts/AuthContext';

watchConnection([axios, apiClient]);
installOffline([axios, apiClient], axios);

const changes = n => `${n} change${n === 1 ? '' : 's'}`;
const clock = ms => new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

// Staff get the detail; gymnasts and parents just get a short heads-up
function message(kind, { pending, savedCopyAt, sentCount }, isStaff) {
  switch (kind) {
    case 'offline':
      if (!isStaff) return { title: 'No connection.', detail: 'Some progress might not be saved.' };
      return {
        title: 'No connection.',
        detail: [
          savedCopyAt && `Showing what was saved at ${clock(savedCopyAt)}.`,
          pending
            ? `${changes(pending)} waiting to send.`
            : "Progress updates, the register, training diaries and routines still work and will be sent when it's back. Other changes won't save.",
        ].filter(Boolean).join(' '),
      };
    case 'slow':
      return {
        title: 'Slow connection.',
        detail: pending ? `${changes(pending)} waiting to send.` : 'Things may take a while to load or save.',
      };
    case 'unavailable':
      return { title: 'Server unavailable.', detail: `${changes(pending)} waiting on this phone. They'll be sent when it's back.` };
    case 'sending':
      return { title: `Sending ${changes(pending)}...` };
    default:
      return { title: 'Back online.', detail: sentCount ? `${changes(sentCount)} sent.` : '' };
  }
}

export default function ConnectionIndicator() {
  const { isStaff } = useAuth();
  const [status, setStatus] = useState(getConnectionStatus);
  const [offline, setOffline] = useState(getOfflineState);
  const [showBack, setShowBack] = useState(false);
  const previous = useRef(status);

  useEffect(() => subscribeOffline(setOffline), []);
  useEffect(() => subscribeConnection(next => {
    if (next === 'online' && previous.current === 'offline') setShowBack(true);
    if (next !== 'online') setShowBack(false);
    previous.current = next;
    setStatus(next);
  }), []);

  // Show "Back online" once everything waiting has gone, then hide
  const sending = status !== 'offline' && offline.pending > 0;
  useEffect(() => {
    if (!showBack || sending) return undefined;
    const t = setTimeout(() => { setShowBack(false); clearSavedCopyNotice(); }, 3000);
    return () => clearTimeout(t);
  }, [showBack, sending]);

  let kind = null;
  if (status === 'offline') kind = 'offline';
  else if (offline.serverUnavailable) kind = 'unavailable';
  else if (status === 'slow') kind = 'slow';
  else if (sending) kind = 'sending';
  else if (showBack) kind = 'back';

  const text = kind && message(kind, offline, isStaff);
  return (
    <div className="connection-indicator-slot">
      <div role="status" aria-live="polite">
        {kind && (
          <div className={`connection-indicator connection-indicator--${kind}`}>
            <span>
              <span className="connection-indicator__dot" aria-hidden="true" />
              <strong>{text.title}</strong>
              {text.detail && ` ${text.detail}`}
            </span>
          </div>
        )}
      </div>
      {offline.failures.length > 0 && (
        <div className="connection-indicator connection-indicator--failed" role="alert">
          <span>{offline.failures.join(' ')}</span>
          <button type="button" className="connection-indicator__close" onClick={dismissFailures} aria-label="Dismiss">×</button>
        </div>
      )}
    </div>
  );
}
